import { createSelector } from 'reselect';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { Selectors } from '@grandvisionhq/configurator';
import { IntlSelectors, uninitState } from '@grandvisionhq/state';
import { isNewPrescriptionAndUpload } from '../../utils/is-new-prescription-upload.js';
import { getLensTypes as getLensTypes$1, getPackages as getPackages$1, getLensAddOns } from '../bundles/selectors.js';
import { getPrescriptionType as getPrescriptionType$1, getPdUploadValidationRequirement as getPdUploadValidationRequirement$1, getSelectedLensType as getSelectedLensType$1, getSelectedPackage as getSelectedPackage$1, getSelectedAddOn as getSelectedAddOn$1, getPrescription as getPrescription$1, getAdditionalPrescriptionInfoFormState as getAdditionalPrescriptionInfoFormState$1, getManualPrescriptionValidationState as getManualPrescriptionValidationState$1, getPrescriptionUploadValidationState, getVerificationAllowed as getVerificationAllowed$1 } from '../configurations/selectors.js';
import { getLensTypeSku, getPackageSku } from '../configurations/selectors.internal.js';
import { convertToFrameInfo } from '../helpers.js';
import { getFrameImage as getFrameImage$1 } from '../products/selectors.js';
import { getFrames } from '../products/selectors.internal.js';
import { configuratorIdProp } from '../selectors.internal.js';
import { getConfigurator } from './selectors.internal.js';

/**
 * Exported selectors to be used in container components
 */ const getIsExtraValidationStepEnabled = createSelector(getConfigurator, (c)=>c.isExtraValidationStepEnabled
);
const getRxEntryTypes = createSelector(getConfigurator, (c)=>c.rxEntryTypes
);
const getBookAppointmentRoute = createSelector(getConfigurator, (c)=>c.bookAppointmentRoute
);
const getFrameSku = createSelector(getConfigurator, (c)=>{
    return c === null || c === void 0 ? void 0 : c.frameSku;
});
const getFrameImage = createSelector(getFrameSku, (s)=>s
, (_, props)=>props
, (frameSku, state, frameImageProps)=>{
    if (!frameSku) {
        return undefined;
    }
    const { width  } = frameImageProps;
    return getFrameImage$1(state, {
        frameSku,
        width
    });
});
/**
 * Returns the FrameInfo of the frame that's currently being configured
 */ const getSelectedFrame = createSelector(getFrameSku, getFrames, IntlSelectors.getLocale, (frameSku, frames, locale)=>{
    if (!frameSku || !locale) {
        return undefined;
    }
    const frame = frames[frameSku];
    if (!frame) {
        return undefined;
    }
    return convertToFrameInfo(frame, frameSku, locale);
});
const getSelectedFrameProductType = createSelector(getFrameSku, getFrames, (frameSku, frames)=>{
    var ref1;
    if (!frameSku) {
        return;
    }
    const frame = frames[frameSku];
    if (!frame) {
        return;
    }
    return (ref1 = frame.productType.obj) === null || ref1 === void 0 ? void 0 : ref1.name;
});
/**
 * Get the prescription type enum value
 */ const getPrescriptionType = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getPrescriptionType$1(state, {
        frameSku
    });
});
const getPdUploadValidationRequirement = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getPdUploadValidationRequirement$1(state, {
        frameSku
    });
});
/**
 * The currently selected lens-type product the frame that's being configured
 */ const getSelectedLensType = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getSelectedLensType$1(state, {
        frameSku
    });
});
/**
 * Returns the available lens-types for the frame that's currently being configured
 */ const getLensTypes = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    return getLensTypes$1(state, {
        frameSku
    });
});
/**
 * The currently selected package product for the frame that's being configured
 */ const getSelectedPackage = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getSelectedPackage$1(state, {
        frameSku
    });
});
const getPackages = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku) {
        return uninitState();
    }
    return getPackages$1(state, {
        lensTypeSku
    });
});
/**
 * The currently selected add-on product for the frame that's being configured
 */ const getSelectedAddOn = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getSelectedAddOn$1(state, {
        frameSku
    });
});
const getAddOns = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    const packageSku = getPackageSku(state, {
        frameSku
    });
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku || !packageSku) {
        return uninitState();
    }
    return getLensAddOns(state, {
        packageSku
    });
});
/**
 * The prescription parameter values as entered on the current frame / lens
 */ const getPrescription = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku) {
        return uninitState();
    }
    return getPrescription$1(state, {
        frameSku,
        lensTypeSku
    });
});
/**
 * The prescription info as entered on the current frame / lens
 */ const getAdditionalPrescriptionInfoFormState = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return {};
    }
    return getAdditionalPrescriptionInfoFormState$1(state, {
        frameSku
    });
});
/**
 * Get the validation state of the manual prescription form
 */ const getManualPrescriptionValidationState = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku) {
        return undefined;
    }
    return getManualPrescriptionValidationState$1(state, {
        frameSku,
        lensTypeSku
    });
});
/**
 * Get the validation state of the Prescription Step,
 * considering the currently selected prescriptionType
 */ const getIsPrescriptionUploadStepValid = createSelector(getFrameSku, getPrescriptionType, (s)=>s
, (frameSku, prescriptionType, state)=>{
    if (!frameSku) {
        return undefined;
    }
    if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
        var ref2;
        return (ref2 = getPrescriptionUploadValidationState(state, {
            frameSku
        })) === null || ref2 === void 0 ? void 0 : ref2.isValid;
    }
    return undefined;
});
const getIsPrescriptionFormStepValid = createSelector(getFrameSku, getPrescriptionType, (s)=>s
, (frameSku, prescriptionType, state)=>{
    if (!frameSku) {
        return undefined;
    }
    if (CommerceToolsApi.isNewPrescription(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
        var ref3;
        return (ref3 = getManualPrescriptionValidationState(state)) === null || ref3 === void 0 ? void 0 : ref3.isValid;
    }
    return undefined;
});
/**
 * Step related selectors
 */ const hasVisitedStepPrescription = createSelector((s)=>s
, configuratorIdProp, (s, id)=>Selectors.hasVisitedStep(s, {
        id,
        stepName: 'prescription'
    })
);
const hasVisitedStepLensPackage = createSelector((s)=>s
, configuratorIdProp, (s, id)=>Selectors.hasVisitedStep(s, {
        id,
        stepName: 'lensPackage'
    })
);
const hasVisitedStepAddOn = createSelector((s)=>s
, configuratorIdProp, (s, id)=>Selectors.hasVisitedStep(s, {
        id,
        stepName: 'lensAddOn'
    })
);
/**
 * Calculates the total price and total discounted price for the current configuration
 */ const getBundlePrice = createSelector(getSelectedFrame, getSelectedLensType, getSelectedPackage, hasVisitedStepLensPackage, getSelectedAddOn, hasVisitedStepAddOn, (frame, lensType, lensPackage, hasVisitedStepLensPackage1, addOn, hasVisitedStepAddOn1)=>{
    var ref25, ref4, ref5, ref6, ref7, ref8, ref9, ref10, ref11, ref12, ref13, ref14;
    if (!frame || !frame.price) {
        return undefined;
    }
    var ref15, ref16, ref17;
    const bundlePrices = [
        frame.price.originalPrice,
        (ref15 = lensType === null || lensType === void 0 ? void 0 : (ref25 = lensType.price) === null || ref25 === void 0 ? void 0 : ref25.originalPrice) !== null && ref15 !== void 0 ? ref15 : 0,
        hasVisitedStepLensPackage1 ? (ref16 = lensPackage === null || lensPackage === void 0 ? void 0 : (ref4 = lensPackage.price) === null || ref4 === void 0 ? void 0 : ref4.originalPrice) !== null && ref16 !== void 0 ? ref16 : 0 : 0,
        hasVisitedStepAddOn1 ? (ref17 = addOn === null || addOn === void 0 ? void 0 : (ref5 = addOn.price) === null || ref5 === void 0 ? void 0 : ref5.originalPrice) !== null && ref17 !== void 0 ? ref17 : 0 : 0, 
    ];
    const originalPrice = bundlePrices.reduce((sum, current)=>sum + current
    , 0);
    const hasDiscounts = [
        frame.price.discountedPrice,
        lensType === null || lensType === void 0 ? void 0 : (ref6 = lensType.price) === null || ref6 === void 0 ? void 0 : ref6.discountedPrice,
        hasVisitedStepLensPackage1 && (lensPackage === null || lensPackage === void 0 ? void 0 : (ref7 = lensPackage.price) === null || ref7 === void 0 ? void 0 : ref7.discountedPrice),
        hasVisitedStepAddOn1 && (addOn === null || addOn === void 0 ? void 0 : (ref8 = addOn.price) === null || ref8 === void 0 ? void 0 : ref8.discountedPrice), 
    ].filter((p)=>typeof p === 'number' && !isNaN(p)
    ).length > 0;
    var _discountedPrice, ref18, ref19, ref20, ref21, ref22, ref23, ref24;
    const bundlePricesDiscounted = hasDiscounts ? [
        (ref18 = (_discountedPrice = frame.price.discountedPrice) !== null && _discountedPrice !== void 0 ? _discountedPrice : frame.price.originalPrice) !== null && ref18 !== void 0 ? ref18 : 0,
        (ref20 = (ref19 = lensType === null || lensType === void 0 ? void 0 : (ref9 = lensType.price) === null || ref9 === void 0 ? void 0 : ref9.discountedPrice) !== null && ref19 !== void 0 ? ref19 : lensType === null || lensType === void 0 ? void 0 : (ref10 = lensType.price) === null || ref10 === void 0 ? void 0 : ref10.originalPrice) !== null && ref20 !== void 0 ? ref20 : 0,
        hasVisitedStepLensPackage1 ? (ref22 = (ref21 = lensPackage === null || lensPackage === void 0 ? void 0 : (ref11 = lensPackage.price) === null || ref11 === void 0 ? void 0 : ref11.discountedPrice) !== null && ref21 !== void 0 ? ref21 : lensPackage === null || lensPackage === void 0 ? void 0 : (ref12 = lensPackage.price) === null || ref12 === void 0 ? void 0 : ref12.originalPrice) !== null && ref22 !== void 0 ? ref22 : 0 : 0,
        hasVisitedStepAddOn1 ? (ref24 = (ref23 = addOn === null || addOn === void 0 ? void 0 : (ref13 = addOn.price) === null || ref13 === void 0 ? void 0 : ref13.discountedPrice) !== null && ref23 !== void 0 ? ref23 : addOn === null || addOn === void 0 ? void 0 : (ref14 = addOn.price) === null || ref14 === void 0 ? void 0 : ref14.originalPrice) !== null && ref24 !== void 0 ? ref24 : 0 : 0, 
    ] : [];
    const discountedPrice = hasDiscounts ? bundlePricesDiscounted.reduce((sum, current)=>sum + current
    , 0) : undefined;
    return {
        originalPrice,
        discountedPrice,
        currency: frame.price.currency
    };
});
const getVerificationAllowed = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getVerificationAllowed$1(state, {
        frameSku
    });
});
var ref;
/**
 * Returns a boolean whether the currently selected package allows add ons to be optional
 * The default value is always `true` unless it is specifically set to `false` in the product data
 */ const getAreAddOnsOptional = createSelector(getSelectedPackage, (p)=>{
    return (ref = p === null || p === void 0 ? void 0 : p.areAddOnsOptional) !== null && ref !== void 0 ? ref : true;
});

export { getAddOns, getAdditionalPrescriptionInfoFormState, getAreAddOnsOptional, getBookAppointmentRoute, getBundlePrice, getFrameImage, getFrameSku, getIsExtraValidationStepEnabled, getIsPrescriptionFormStepValid, getIsPrescriptionUploadStepValid, getLensTypes, getManualPrescriptionValidationState, getPackages, getPdUploadValidationRequirement, getPrescription, getPrescriptionType, getRxEntryTypes, getSelectedAddOn, getSelectedFrame, getSelectedFrameProductType, getSelectedLensType, getSelectedPackage, getVerificationAllowed, hasVisitedStepAddOn, hasVisitedStepLensPackage, hasVisitedStepPrescription };
//# sourceMappingURL=selectors.js.map
